/**
 * DriverCorrectionModal — OPEN-157: correct the driver recorded on a finished ticket ("the
 * wrong person was signed in").
 *
 * POST /v1/jobs/:id/driver-correction { driverId, reason (10+) }. REVIEW_TICKET roles (the
 * server is the gate). Only the ticket's driver changes: the hours stay with whoever was
 * clocked in, and a ticket on an invoice is refused until the invoice is voided.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiOpts, useApiMutation } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { JOB_DETAIL_QUERY_KEY } from './use-job-detail';
import { JOB_AUDIT_QUERY_KEY } from './use-completed-jobs';
import { DRIVER_CORRECTION_REASON_MIN, driverCorrectionErrorMessage } from './driver-correction';

interface CorrectionBody {
  driverId: string;
  reason: string;
}

export interface DriverCorrectionModalProps {
  readonly jobId: string | null;
  readonly haulId: string | null;
  readonly driverId: string | null;
  readonly driverName: string | null;
  readonly drivers: ReadonlyArray<{ readonly driverId: string; readonly driverName: string }>;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCorrected: () => void;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

export function DriverCorrectionModal({
  jobId,
  haulId,
  driverId,
  driverName,
  drivers,
  open,
  onClose,
  onCorrected,
}: DriverCorrectionModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [toDriver, setToDriver] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setToDriver('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const mutation = useApiMutation<CorrectionBody, { fromDriverId: string | null }>(
    apiOpts,
    `/v1/jobs/${jobId ?? ''}/driver-correction`,
    'POST',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['jobs'] });
        // In Review (list and detail) names the driver too, and the timeline gains the row.
        void queryClient.invalidateQueries({ queryKey: ['inreview'] });
        if (jobId) {
          void queryClient.invalidateQueries({ queryKey: JOB_DETAIL_QUERY_KEY(jobId) });
          void queryClient.invalidateQueries({ queryKey: JOB_AUDIT_QUERY_KEY(jobId) });
        }
        toast.success('Driver corrected');
        onCorrected();
      },
      onError: (err: unknown) => {
        setError(driverCorrectionErrorMessage(err));
      },
    },
  );

  const options = drivers
    .filter((d) => d.driverId !== driverId)
    .map((d) => ({ value: d.driverId, label: d.driverName }));
  const reasonValid = reason.trim().length >= DRIVER_CORRECTION_REASON_MIN;
  const canSubmit = toDriver !== '' && reasonValid && !mutation.isPending && jobId !== null;

  const handleConfirm = (): void => {
    if (!canSubmit) return;
    setError(null);
    mutation.mutate({ driverId: toDriver, reason: reason.trim() });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-md"
        data-testid="driver-correction-modal"
      >
        <DialogHeader>
          <DialogTitle>{`Correct the driver on ${haulId ?? 'this ticket'}`}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.4 }}>
            {`The ticket now names ${driverName ?? 'no driver'}. Correcting it changes the driver on the ticket, the run ticket printout and the invoice. The hours on the timeclock stay with whoever was clocked in.`}
          </p>
          <div>
            <label htmlFor="driver-correction-driver" style={labelStyle}>
              Hauled by
            </label>
            <Combobox
              id="driver-correction-driver"
              value={toDriver}
              onChange={setToDriver}
              options={options}
              placeholder="Choose a driver"
              searchPlaceholder="Search drivers…"
              emptyText="No drivers match."
            />
          </div>
          <div>
            <label htmlFor="driver-correction-reason" style={labelStyle}>
              Reason
            </label>
            <textarea
              id="driver-correction-reason"
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              placeholder={`Why is the driver being corrected? (min ${DRIVER_CORRECTION_REASON_MIN} characters)`}
              style={textareaStyle}
              aria-label="Driver correction reason"
            />
            {reason.trim().length > 0 && !reasonValid && (
              <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                Reason must be at least {DRIVER_CORRECTION_REASON_MIN} characters.
              </span>
            )}
          </div>
        </div>
        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="driver-correction-confirm"
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            {mutation.isPending ? 'Correcting…' : 'Correct driver'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
